// A card charge still being checked (QA-307), where the invoice is: the Money tab, the invoice sheet and
// Record payment (books/RecordPayment.tsx, Receivables' too). Check again asks the processor; Cancel this
// charge… asks it to cancel. Was MoneyActions.tsx's (it re-exports it); moved unchanged so Record payment
// draws it without the Money tab's sheet (BL-189). Blueprint draws it with ui-web's Links and ConfirmDialog
// and its own strict sheet (card-being-checked.css); Riverstone keeps its classes (history/money-actions.css)
// and MoneySheet.
import { RpcError } from '@flowology/client';
import { ConfirmDialog, Link } from '@flowology/ui-web/fl';
import { useState } from 'react';
import { client } from '../api.ts';
import { useDesign } from '../blueprint/design.ts';
import type { CardCheck } from '../history/history-model.ts';
import { cardAnswerWords, cardCheckWords, type CardAnswer } from '../history/money-model.ts';
import { MoneySheet } from './MoneySheet.tsx';
import './card-being-checked.css';

const say = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'That didn’t go through. Try again.';
const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

/**
 * While a card charge on an invoice is unknown nothing else is taken on it (QA-307), so the office
 * sees the charge where it sees the invoice, and asks the processor again or cancels the charge.
 */
export function CardBeingChecked({
  check,
  timeZone,
  canAct,
  onDone,
}: {
  check: CardCheck;
  /** The company's, to say when the charge was started (when that is known). */
  timeZone?: string;
  /** payments.record: the office that takes payments. */
  canAct: boolean;
  onDone: (m: string) => void;
}) {
  const bp = useDesign() === 'blueprint';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const ask = async (asked: 'check' | 'cancel') => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<CardAnswer>(
        asked === 'check' ? 'payments.checkCard' : 'payments.cancelUnknown',
        { checkoutId: check.checkoutId },
      );
      setBusy(false);
      setCancelling(false);
      onDone(cardAnswerWords(r, asked));
    } catch (e) {
      setBusy(false);
      setError(say(e));
    }
  };
  const cancelWords = (
    <>
      Asks the card processor to cancel the charge
      {check.amountCents !== undefined ? ` of ${cad(check.amountCents)}` : ''}, so the customer can pay
      another way. If it already went through, it is recorded instead. Until the processor confirms the
      cancel, the invoice still takes no other payment.
    </>
  );
  // Blueprint (BL-189): Check again and Cancel this charge… are Links, the cancel ConfirmDialog; the
  // same calls, words and outcomes
  if (bp)
    return (
      <div className="bp-cardcheck">
        <span className="bp-cardcheck__words">{cardCheckWords(check, timeZone)}</span>
        {canAct && (
          <span className="bp-cardcheck__acts">
            <Link isDisabled={busy} onPress={() => void ask('check')}>
              Check again
            </Link>
            <Link isDisabled={busy} onPress={() => setCancelling(true)}>
              Cancel this charge…
            </Link>
          </span>
        )}
        {error && !cancelling && (
          <span className="bp-cardcheck__bad" role="alert">
            {error}
          </span>
        )}
        <ConfirmDialog
          isOpen={cancelling}
          onClose={() => {
            setCancelling(false);
            setError(null);
          }}
          title="Cancel this card charge"
          confirmLabel="Cancel this charge"
          cancelLabel="Cancel"
          failText="That didn’t go through. Try again."
          onConfirm={async () => {
            const r = await client.call<CardAnswer>('payments.cancelUnknown', {
              checkoutId: check.checkoutId,
            });
            onDone(cardAnswerWords(r, 'cancel'));
          }}
        >
          {cancelWords}
        </ConfirmDialog>
      </div>
    );
  return (
    <div className="card-check">
      <span className="small block">{cardCheckWords(check, timeZone)}</span>
      {canAct && (
        <span className="row money-acts">
          <button className="btn quiet" disabled={busy} onClick={() => void ask('check')}>
            Check again
          </button>
          <button className="btn quiet" disabled={busy} onClick={() => setCancelling(true)}>
            Cancel this charge…
          </button>
        </span>
      )}
      {error && !cancelling && (
        <span className="error small block" role="alert">
          {error}
        </span>
      )}
      {cancelling && (
        <MoneySheet
          title="Cancel this card charge"
          outcome={cancelWords}
          confirm="Cancel this charge"
          ready
          busy={busy}
          error={error}
          onConfirm={() => void ask('cancel')}
          onClose={() => {
            setCancelling(false);
            setError(null);
          }}
        />
      )}
    </div>
  );
}
